import React, { useState, useEffect } from 'react';
import { Box, Tab, Tabs, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Alert, Typography, TextField, Button, CircularProgress, FormControlLabel, Switch, Grid } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import BusinessDetailsTab from './BusinessDetailsTab';
import BusinessRolesTab from './BusinessRolesTab';
import AppointmentTypesTab from './AppointmentTypesTab';
import BusinessUsersTab from './BusinessUsersTab';
import { businessService, Business } from '../../services/businessService';
import { BusinessDetailsDto } from '../../types/business';
import { DEFAULT_THEME, getThemeValue } from '../../constants/themeDefaults';
import { MuiColorInput as ColorInput } from 'mui-color-input';
import { useBusinessTheme } from '../../contexts/BusinessThemeContext';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`business-settings-tabpanel-${index}`}
            aria-labelledby={`business-settings-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

interface BusinessClosure {
    id: string;
    description: string;
    startDate: string;
    endDate: string;
    isRecurring: boolean;
}

interface BusinessFormData {
    id: string;
    name: string;
    description: string;
    address: string;
    timeZoneId: string;
    phone: string;
    email: string;
    website: string;
    useBusinessHours: boolean;
    businessHoursStart: string;
    businessHoursEnd: string;
    restrictToBusinessHours: boolean;
    backgroundColor: string;
    primaryColor: string;
    secondaryColor: string;
    textColor: string;
    accentColor: string;
    navigationColor: string;
    firstDayOfWeek: number;
    headingColor: string;
    subtleAccent: string;
    defaultCity: string;
    defaultCountry: string;
    defaultAddress: string;
    defaultLatitude: number;
    defaultLongitude: number;
}

const NEW_BUSINESS_ID = 'new';

interface BusinessSettingsProps {
    initialTab?: number;
    onTabChange?: (tabIndex: number) => void;
    showOnlyGeneralTab?: boolean;
}

const BusinessSettings: React.FC<BusinessSettingsProps> = ({ 
    initialTab = 0, 
    onTabChange,
    showOnlyGeneralTab = false
}) => {
    const [activeTab, setActiveTab] = useState(initialTab);
    const { user } = useAuth();
    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');
    const [formData, setFormData] = useState<BusinessDetailsDto>({
        id: '',
        name: '',
        description: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        useBusinessHours: false,
        businessHoursStart: null,
        businessHoursEnd: null,
        restrictToBusinessHours: false,
        backgroundColor: DEFAULT_THEME.backgroundColor,
        primaryColor: DEFAULT_THEME.primaryColor,
        secondaryColor: DEFAULT_THEME.secondaryColor,
        navigationColor: DEFAULT_THEME.navigationColor,
        textColor: DEFAULT_THEME.textColor,
        accentColor: DEFAULT_THEME.accentColor,
        subtleAccent: DEFAULT_THEME.subtleAccent,
        defaultCity: '',
        defaultCountry: '',
        defaultAddress: '',
        defaultLatitude: 0,
        defaultLongitude: 0,
        firstDayOfWeek: 1,
        borderColor: DEFAULT_THEME.borderColor
    });
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [timeZones] = useState<string[]>([
        'UTC',
        'America/New_York',
        'America/Chicago',
        'America/Denver',
        'America/Los_Angeles',
        'America/Phoenix',
        'America/Anchorage',
        'Pacific/Honolulu',
        'Europe/London',
        'Europe/Paris',
        'Europe/Berlin',
        'Europe/Moscow',
        'Asia/Dubai',
        'Asia/Shanghai',
        'Asia/Tokyo',
        'Australia/Sydney',
        'Pacific/Auckland'
    ]);
    const { setTheme } = useBusinessTheme();

    const isSiteAdmin = user?.isAdmin;
    const isBusinessAdmin = !isSiteAdmin && user?.businessRoles?.some(role => 
        role.businessId === selectedBusinessId && role.isBusinessAdmin
    );
    const showAdminTabs = isSiteAdmin || isBusinessAdmin;

    useEffect(() => {
        setActiveTab(initialTab);
    }, [initialTab]);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
        if (onTabChange) {
            onTabChange(newValue);
        }
    };

    const handleBusinessChange = async (event: SelectChangeEvent<string>) => {
        const businessId = event.target.value;
        setSelectedBusinessId(businessId);
        
        if (businessId === NEW_BUSINESS_ID) {
            resetForm();
            return;
        }

        try {
            const business = await businessService.getBusinessDetails(businessId);
            setFormData({
                ...business,
                backgroundColor: business.backgroundColor || DEFAULT_THEME.backgroundColor,
                primaryColor: business.primaryColor || DEFAULT_THEME.primaryColor,
                secondaryColor: business.secondaryColor || DEFAULT_THEME.secondaryColor,
                navigationColor: business.navigationColor || DEFAULT_THEME.navigationColor,
                textColor: business.textColor || DEFAULT_THEME.textColor,
                accentColor: business.accentColor || DEFAULT_THEME.accentColor
            });
        } catch (err) {
            setError('Failed to load business details');
        }
    };

    useEffect(() => {
        const loadBusinesses = async () => {
            if (user) {
                try {
                    const data = user.isAdmin 
                        ? await businessService.getAllBusinesses()
                        : await businessService.getBusinesses();
                    setBusinesses(data);
                    if (data.length > 0) {
                        const businessId = data[0].id;
                        setSelectedBusinessId(businessId);
                        
                        const business = await businessService.getBusinessDetails(businessId);
                        setFormData({
                            ...business,
                            backgroundColor: business.backgroundColor || DEFAULT_THEME.backgroundColor,
                            primaryColor: business.primaryColor || DEFAULT_THEME.primaryColor,
                            secondaryColor: business.secondaryColor || DEFAULT_THEME.secondaryColor,
                            navigationColor: business.navigationColor || DEFAULT_THEME.navigationColor,
                            textColor: business.textColor || DEFAULT_THEME.textColor,
                            accentColor: business.accentColor || DEFAULT_THEME.accentColor
                        });
                    }
                } catch (err) {
                    setError('Failed to load businesses');
                }
            }
        };
        loadBusinesses();
    }, [user]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);
            const updatedFormData = {
                ...formData,
                backgroundColor: formData.backgroundColor || DEFAULT_THEME.backgroundColor,
                primaryColor: formData.primaryColor || DEFAULT_THEME.primaryColor,
                secondaryColor: formData.secondaryColor || DEFAULT_THEME.secondaryColor,
                navigationColor: formData.navigationColor || DEFAULT_THEME.navigationColor,
                textColor: formData.textColor || DEFAULT_THEME.textColor,
                accentColor: formData.accentColor || DEFAULT_THEME.accentColor
            };
            await businessService.updateBusinessDetails(updatedFormData);
            setTheme({
                backgroundColor: updatedFormData.backgroundColor,
                primaryColor: updatedFormData.primaryColor,
                secondaryColor: updatedFormData.secondaryColor,
                navigationColor: updatedFormData.navigationColor,
                textColor: updatedFormData.textColor,
                accentColor: updatedFormData.accentColor,
                subtleAccent: updatedFormData.subtleAccent,
                borderColor: updatedFormData.borderColor
            });
            setSuccess('Business details updated successfully');
        } catch (err) {
            setError('Failed to update business details');
            console.error('Error updating business details:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTimeZoneChange = (e: SelectChangeEvent) => {
        setFormData(prev => ({ ...prev, timeZoneId: e.target.value }));
    };

    const resetForm = () => {
        setFormData({
            id: '',
            name: '',
            description: '',
            address: '',
            phone: '',
            email: '',
            website: '',
            timeZoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
            useBusinessHours: false,
            businessHoursStart: null,
            businessHoursEnd: null,
            restrictToBusinessHours: false,
            backgroundColor: DEFAULT_THEME.backgroundColor,
            primaryColor: DEFAULT_THEME.primaryColor,
            secondaryColor: DEFAULT_THEME.secondaryColor,
            navigationColor: DEFAULT_THEME.navigationColor,
            textColor: DEFAULT_THEME.textColor,
            accentColor: DEFAULT_THEME.accentColor,
            subtleAccent: DEFAULT_THEME.subtleAccent,
            defaultCity: '',
            defaultCountry: '',
            defaultAddress: '',
            defaultLatitude: 0,
            defaultLongitude: 0,
            firstDayOfWeek: 1,
            borderColor: DEFAULT_THEME.borderColor
        });
    };

    // Only render if we have a selected business
    if (!selectedBusinessId) {
        return null;
    }

    return (
        <>
            {/* Only show tabs if not showOnlyGeneralTab */}
            {!showOnlyGeneralTab && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange}
                        aria-label="business settings tabs"
                    >
                        <Tab label="Business Details" />
                        <Tab label="Users" disabled={!showAdminTabs || selectedBusinessId === NEW_BUSINESS_ID} />
                        <Tab label="Roles" disabled={!showAdminTabs || selectedBusinessId === NEW_BUSINESS_ID} />
                        <Tab label="Appointment Types" disabled={!showAdminTabs || selectedBusinessId === NEW_BUSINESS_ID} />
                    </Tabs>
                </Box>
            )}
            
            {/* Always render the appropriate tab content */}
            <TabPanel value={activeTab} index={0}>
                <Box>
                    <Typography variant="h6" gutterBottom>Business Settings</Typography>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
                    
                    {selectedBusinessId !== NEW_BUSINESS_ID ? (
                        <BusinessDetailsTab businessId={selectedBusinessId} />
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{
                            '& .MuiTextField-root': {
                                '& .MuiOutlinedInput-root': {
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#1976d2', // Force blue focus border
                                        borderWidth: '2px'
                                    }
                                }
                            }
                        }}>
                            <TextField
                                id="businessName"
                                name="businessName"
                                label="Business Name"
                                value={formData.name}
                                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                fullWidth
                                margin="normal"
                                required
                            />

                            <TextField
                                id="description"
                                name="description"
                                label="Description"
                                value={formData.description}
                                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                fullWidth
                                margin="normal"
                                multiline
                                rows={3}
                            />

                            <TextField
                                id="address"
                                name="address"
                                label="Address"
                                value={formData.address}
                                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                                fullWidth
                                margin="normal"
                            />

                            <TextField
                                id="phone"
                                name="phone"
                                label="Phone"
                                value={formData.phone}
                                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                                fullWidth
                                margin="normal"
                            />

                            <TextField
                                id="email"
                                name="email"
                                label="Email"
                                value={formData.email}
                                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                                fullWidth
                                margin="normal"
                            />

                            <TextField
                                id="website"
                                name="website"
                                label="Website"
                                value={formData.website}
                                onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                                fullWidth
                                margin="normal"
                            />

                            <FormControl fullWidth margin="normal">
                                <InputLabel>Time Zone</InputLabel>
                                <Select
                                    value={formData.timeZoneId}
                                    onChange={handleTimeZoneChange}
                                    label="Time Zone"
                                >
                                    {timeZones.map((zone) => (
                                        <MenuItem key={zone} value={zone}>
                                            {zone}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.useBusinessHours}
                                        onChange={(e) => setFormData(prev => ({ ...prev, useBusinessHours: e.target.checked }))}
                                    />
                                }
                                label="Use Business Hours"
                            />

                            {formData.useBusinessHours && (
                                <>
                                    <TextField
                                        label="Business Hours Start"
                                        type="time"
                                        value={formData.businessHoursStart || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, businessHoursStart: e.target.value }))}
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            step: 300, // 5 min
                                        }}
                                    />

                                    <TextField
                                        label="Business Hours End"
                                        type="time"
                                        value={formData.businessHoursEnd || ''}
                                        onChange={(e) => setFormData(prev => ({ ...prev, businessHoursEnd: e.target.value }))}
                                        fullWidth
                                        margin="normal"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        inputProps={{
                                            step: 300, // 5 min
                                        }}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.restrictToBusinessHours}
                                                onChange={(e) => setFormData(prev => ({ ...prev, restrictToBusinessHours: e.target.checked }))}
                                            />
                                        }
                                        label="Restrict to Business Hours"
                                    />
                                </>
                            )}

                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <ColorInput
                                        value={formData.backgroundColor}
                                        onChange={(color) => setFormData(prev => ({ ...prev, backgroundColor: color }))}
                                        label="Background Color"
                                        helperText="Used for the main background"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ColorInput
                                        value={formData.primaryColor}
                                        onChange={(color) => setFormData(prev => ({ ...prev, primaryColor: color }))}
                                        label="Primary Color"
                                        helperText="Used for primary elements and buttons"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ColorInput
                                        value={formData.secondaryColor}
                                        onChange={(color) => setFormData(prev => ({ ...prev, secondaryColor: color }))}
                                        label="Secondary Color"
                                        helperText="Used for secondary elements and accents"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ColorInput
                                        value={formData.navigationColor}
                                        onChange={(color) => setFormData(prev => ({ ...prev, navigationColor: color }))}
                                        label="Navigation Color"
                                        helperText="Used for navigation elements"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ColorInput
                                        value={formData.textColor}
                                        onChange={(color) => setFormData(prev => ({ ...prev, textColor: color }))}
                                        label="Text Color"
                                        helperText="Used for text content"
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <ColorInput
                                        value={formData.accentColor}
                                        onChange={(color) => setFormData(prev => ({ ...prev, accentColor: color }))}
                                        label="Accent Color"
                                        helperText="Used for highlights and emphasis"
                                    />
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 3 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={loading}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Save Changes'}
                                </Button>
                            </Box>
                        </Box>
                    )}
                </Box>
            </TabPanel>
            <TabPanel value={activeTab} index={1}>
                {showAdminTabs && selectedBusinessId !== NEW_BUSINESS_ID && <BusinessUsersTab businessId={selectedBusinessId} />}
            </TabPanel>
            <TabPanel value={activeTab} index={2}>
                {showAdminTabs && selectedBusinessId !== NEW_BUSINESS_ID && <BusinessRolesTab businessId={selectedBusinessId} />}
            </TabPanel>
            <TabPanel value={activeTab} index={3}>
                {showAdminTabs && selectedBusinessId !== NEW_BUSINESS_ID && <AppointmentTypesTab businessId={selectedBusinessId} />}
            </TabPanel>
        </>
    );
};

export default BusinessSettings; 